import React, { FC } from 'react'
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material'
import { useFormik, FormikProvider, Form } from 'formik'
import { FormDropdown } from 'src/components/ui'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { SensorState } from 'src/api'
import { capitalizeFirstLetter } from 'src/utils/formatters'
import { DefaultsEntry } from 'src/components/providers/queryParamsProvider'

export const batteryStatsQueryParamDefaults: DefaultsEntry[] = []

const activationStatusOptions = [
  { name: 'All', value: 'all' },
  { name: 'Active', value: 'active' },
  { name: 'Deactive', value: 'deactive' }
]

const availableInThingspaceOptions = [
  { name: 'All', value: 'all' },
  { name: 'Available', value: 'true' },
  { name: 'Not available', value: 'false' }
]

interface BatteryStatsListFiltersProps {
  states?: SensorState[]
}

export const BatteryStatsListFilters: FC<BatteryStatsListFiltersProps> = ({ states = [] }) => {
  const [queryParams, setQueryParams] = useQueryParams()

  const formik = useFormik({
    initialValues: {
      [SearchParamsKeys.ActivationStatus]: queryParams[SearchParamsKeys.ActivationStatus] || 'all',
      [SearchParamsKeys.IsAvailableInThingspace]: queryParams[SearchParamsKeys.IsAvailableInThingspace] || 'all',
      [SearchParamsKeys.State]: queryParams[SearchParamsKeys.State]?.split(',') || []
    },
    onSubmit: async (formState) => {
      const activationStatus = formState[SearchParamsKeys.ActivationStatus] || 'all'
      const isAvailableInThingspace = formState[SearchParamsKeys.IsAvailableInThingspace] || 'all'
      const state: string[] = formState[SearchParamsKeys.State] || []
      await setQueryParams(
        {
          [SearchParamsKeys.ActivationStatus]: activationStatus,
          [SearchParamsKeys.IsAvailableInThingspace]: isAvailableInThingspace,
          ...(state.length > 0 ? { [SearchParamsKeys.State]: state.join(',') } : {})
        },
        [SearchParamsKeys.ActivationStatus, SearchParamsKeys.IsAvailableInThingspace, SearchParamsKeys.State]
      )
    }
  })

  const { submitForm, values, setFieldValue } = formik

  const handleActivationStatusChanges = (event: SelectChangeEvent<typeof values['activationStatus']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.ActivationStatus, value)
    submitForm()
  }

  const handleAvailableInThingspaceChanges = (event: SelectChangeEvent<typeof values['isAvailableInThingspace']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.IsAvailableInThingspace, value)
    submitForm()
  }

  const handleStateChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.State, typeof value === 'string' ? value.split(',') : value)
  }

  const handleStateClose = () => submitForm()

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container alignItems="center" spacing={4} sx={{ minHeight: 103 }}>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: { xs: '100%', sm: 400 } }}>
              <InputLabel id="activation-status-chip-label">Activation status</InputLabel>
              <Select
                labelId="activation-status-chip-label"
                id="activation-status-chip"
                value={values[SearchParamsKeys.ActivationStatus] || 'all'}
                onChange={handleActivationStatusChanges}
                input={<OutlinedInput id="activation-status-select-chip" label="Activation status" />}
                renderValue={(selected) => {
                  const item = activationStatusOptions.find((o) => o.value === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={item?.value} label={item?.name} />
                    </Box>
                  )
                }}
              >
                {activationStatusOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <FormControl sx={{ m: 1, width: { xs: '100%', sm: 400 } }}>
              <InputLabel id="available-in-thingspace-chip-label">Available in ThingSpace</InputLabel>
              <Select
                labelId="available-in-thingspace-chip-label"
                id="available-in-thingspace-chip"
                value={values[SearchParamsKeys.IsAvailableInThingspace] || 'all'}
                onChange={handleAvailableInThingspaceChanges}
                input={<OutlinedInput id="available-in-thingspace-select-chip" label="Available in ThingSpace" />}
                renderValue={(selected) => {
                  const selectedItem = availableInThingspaceOptions.find((item) => `${item.value}` === selected)
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip key={`${selectedItem?.value}`} label={selectedItem?.name} />
                    </Box>
                  )
                }}
              >
                {availableInThingspaceOptions.map((item) => (
                  <MenuItem key={`${item?.value}`} value={`${item?.value}`}>
                    {item?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item width={{ xs: '100%', sm: '300px' }}>
            <FormDropdown
              name={SearchParamsKeys.State}
              label="Sensor state"
              list={states}
              menuItemFactory={(item: SensorState) => (
                <MenuItem key={item.id} value={`${item.id}`}>
                  {capitalizeFirstLetter(item.name)}
                </MenuItem>
              )}
              onChange={handleStateChange}
              onClose={handleStateClose}
              multiple
            />
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  )
}
